import { Button } from "@ancestors/components/Button";
import { useLocalStorage } from "@ancestors/components/useLocalStorage";
import { cn } from "@ancestors/components/utils";
import { getUserLocale } from "get-user-locale";
import { LanguagesIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, use, useCallback, useMemo, useState } from "react";
import type { ScriptKey, ScriptLang } from "virtual:script";
import { scripts } from "virtual:script";
import type { ScriptContextValue } from "./scriptContext";
import { ScriptContext } from "./scriptContext";
import { FullPageLoader } from "~/components/FullPageLoader";

const LOCALSTORAGE_LANG_KEY = "ancestors:language";

const availableLanguages = Object.keys(scripts);
const defaultLanguage = availableLanguages.includes("en") ? "en" : availableLanguages[0];

function detectLanguage() {
  const locale = getUserLocale();
  const preferredLanguage = locale.split("-")[0]; // Locale can be smth like `en-US`
  const language = availableLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : defaultLanguage;

  return language as ScriptLang;
}

interface ScriptProviderProps {
  children: ReactNode;
  override?: Record<string, string>;
  disableLanguagePicker?: boolean;
}

interface Script {
  text: (key: ScriptKey) => string;
  html: (key: ScriptKey) => string;
  key: (key: ScriptKey) => string;
}

const EMPTY = {};

export function ScriptProvider({
  override: dynamicContent = EMPTY,
  children,
  disableLanguagePicker,
}: ScriptProviderProps) {
  // Selected language
  const [language, setLanguage] = useLocalStorage(LOCALSTORAGE_LANG_KEY, detectLanguage());
  const [languagePickerVariant, setLanguagePickerVariant] = useState<"subtle" | "labeled">(
    "subtle",
  );

  // Guard against invalid languages that might be stored in localStorage
  if (!availableLanguages.includes(language)) {
    setLanguage(detectLanguage());
  }

  // Lazily load the script content for the selected language
  const scriptResource = useMemo<Promise<Script>>(
    () =>
      (async () => {
        const script = await scripts[language].load();

        let dynamicHtmlContent: Record<string, string> = {};

        if (Object.keys(dynamicContent).length) {
          const { markdownToHTML } = await import("~/lib/markdownToHtml");

          dynamicHtmlContent = Object.fromEntries(
            await Promise.all(
              Object.entries(dynamicContent).map(async ([key, textContent]) => [
                key,
                await markdownToHTML(textContent),
              ]),
            ),
          );
        }

        return {
          text: (key: ScriptKey) => dynamicContent[key] ?? script.text(key),
          html: (key: ScriptKey) => dynamicHtmlContent[key] ?? script.html(key),
          key: (key: ScriptKey) => (key in dynamicContent ? key : script.key(key)),
        };
      })(),
    [dynamicContent, language],
  );

  return (
    <Suspense fallback={<FullPageLoader />}>
      <ScriptLanguagePickerWrapper
        disableLanguagePicker={disableLanguagePicker}
        languagePickerVariant={languagePickerVariant}
        language={language}
        setLanguage={setLanguage}
      >
        <ScriptContextProvider
          scriptResource={scriptResource}
          language={language}
          setLanguagePickerVariant={setLanguagePickerVariant}
        >
          {children}
        </ScriptContextProvider>
      </ScriptLanguagePickerWrapper>
    </Suspense>
  );
}

function ScriptLanguagePickerWrapper({
  children,
  disableLanguagePicker,
  languagePickerVariant,
  language,
  setLanguage,
}: {
  children: ReactNode;
  disableLanguagePicker?: boolean;
  languagePickerVariant: "subtle" | "labeled";
  language: ScriptLang;
  setLanguage: (language: ScriptLang) => void;
}) {
  // Language picker
  const [languagePickerState, setLanguagePickerState] = useState<"open" | "closed">("closed");

  const toggleLanguagePicker = useCallback(() => {
    setLanguagePickerState((s) => (s === "open" ? "closed" : "open"));
  }, []);

  const handleSelectLanguage = useCallback(
    (language: ScriptLang) => {
      setLanguage(language);
      setLanguagePickerState("closed");
    },
    [setLanguage],
  );

  return disableLanguagePicker ? (
    children
  ) : (
    // `grid-rows-1` keeps the row at the height of the screen. An auto row grows with the content
    // instead, so a screen's own scroll container (e.g. `Container`) never overflows and, because
    // of its `overscroll-contain`, doesn't pass the scroll on to `<main>` either
    <div className="grid h-full w-full grid-rows-1 *:col-span-full *:row-span-full">
      {children}

      {availableLanguages.length > 1 && (
        <div
          className={cn(
            "z-10 flex flex-col bg-transparent p-2 transition-colors",
            languagePickerState === "closed" && "pointer-events-none",
            languagePickerState === "open" && "bg-background",
          )}
        >
          <Button
            variant="ghost"
            size={languagePickerVariant === "labeled" ? "sm" : "icon"}
            className="pointer-events-auto flex gap-2 self-end"
            onClick={toggleLanguagePicker}
          >
            {languagePickerState === "closed" ? <LanguagesIcon /> : <XIcon />}
            {languagePickerVariant === "labeled" && <span>Select language</span>}
          </Button>

          <div
            className={cn(
              "grid flex-1 content-center items-center justify-center gap-y-2 transition-opacity",
              languagePickerState === "closed" ? "opacity-0" : "opacity-100",
            )}
          >
            <p className="text-lg font-medium">Select your language:</p>
            {Object.entries(scripts).map(([languageTag, { languageLabel }]) => (
              <Button
                key={languageTag}
                variant={language === languageTag ? "default" : "secondary"}
                onClick={() => handleSelectLanguage(languageTag as ScriptLang)}
              >
                {languageLabel}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScriptContextProvider({
  children,
  scriptResource,
  language,
  setLanguagePickerVariant,
}: {
  children: ReactNode;
  scriptResource: Promise<Script>;
  language: ScriptLang;
  setLanguagePickerVariant: (variant: "subtle" | "labeled") => void;
}) {
  // Suspend while the script is loading
  const script = use(scriptResource);

  const contextValue = useMemo<ScriptContextValue>(
    () => ({ script, language, setLanguagePickerVariant }),
    [language, script, setLanguagePickerVariant],
  );

  return <ScriptContext value={contextValue}>{children}</ScriptContext>;
}
